import {
  FormattedDate as IntlFormattedDate,
  FormattedMessage,
  useIntl,
  type IntlShape,
} from 'react-intl';
import { css } from '@understory-io/leaf/css';
import { useIncludeTaxInPrices } from '~/lib/use-include-tax-in-prices';

export type FormattableLocation = {
  /** Public display-name parts (`Location.displayName`). */
  displayName: string[];
  /** `address.city`; enables the `city` variant. */
  city?: string | null;
};

/**
 * The ONLY sanctioned location formatting in the app (see CLAUDE.md).
 * `full` joins the display-name parts; `city` uses the city and falls back
 * to `full` when the location has none.
 */
export function formatLocation(
  location: FormattableLocation,
  variant: 'full' | 'city' = 'full'
): string {
  if (variant === 'city' && location.city) return location.city;
  return location.displayName.join(', ');
}

export function FormattedLocation({
  location,
  variant = 'full',
}: {
  location: FormattableLocation;
  variant?: 'full' | 'city';
}) {
  return formatLocation(location, variant);
}

/**
 * Which variant a set of selectable locations should use for their labels:
 * `city` only when every location has a city and no two share one —
 * otherwise city labels can't be told apart and `full` is used.
 */
export function locationOptionsVariant(
  locations: readonly Pick<FormattableLocation, 'city'>[]
): 'full' | 'city' {
  const cities = locations.map((l) => l.city).filter((c): c is string => !!c);
  if (cities.length !== locations.length) return 'full';
  return new Set(cities).size === cities.length ? 'city' : 'full';
}

/**
 * The distinct labels to display for a set of locations — one per place, in
 * insertion order, deduped on the rendered label. Under the default `city`
 * variant several addresses in the same city collapse into a single label
 * (e.g. two Aarhus addresses → one "Aarhus C"). Formatting goes through
 * `formatLocation`, keeping all location formatting in this one module.
 */
export function formatLocationLabels(
  locations: readonly FormattableLocation[],
  variant: 'full' | 'city' = 'city'
): string[] {
  return Array.from(
    new Set(locations.map((location) => formatLocation(location, variant)))
  );
}

/**
 * Parse a time string (ISO datetime or Unix ms timestamp) to a Date.
 *
 * Naive ISO datetimes like `"2026-07-30T15:00:00"` (no `Z`, no offset) are
 * interpreted as UTC. Without this, `new Date()` treats them as local time,
 * which differs between the Vercel SSR runtime (UTC) and the user's browser
 * timezone — producing hydration mismatches where the rendered time flashes
 * from the server's value to the client's on hydration.
 */
export function parseTime(value: string): Date {
  if (/^\d+$/.test(value)) {
    return new Date(Number(value));
  }
  const hasTime = value.includes('T');
  const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasTime && !hasTimezone ? `${value}Z` : value);
}

/**
 * Formats a time of day as `"09.05"` / `"09:05"` / `"05:30 PM"` (per locale).
 *
 * Always requests `hour: '2-digit'` — never `'numeric'`. Engines disagree on
 * whether `'numeric'` pads single-digit hours (Node renders "9.05" where
 * Firefox renders "09.05"), and since this code runs during both SSR and
 * hydration, that drift made the server HTML mismatch the client render and
 * React threw hydration error #418 (US-10915). `'2-digit'` is the one hour
 * width every engine agrees on.
 *
 * Also pins `hourCycle` and normalises whitespace, because SSR output must be
 * byte-identical to what every client engine renders (US-11224):
 *
 * - Browsers carry the OS-level 12/24-hour clock setting into `Intl` (as a
 *   `-u-hc-*` locale extension); Node has no such preference. An explicit
 *   `hourCycle` wins over the extension, so both sides render the locale's
 *   canonical clock regardless of device settings.
 * - CLDR ≥42 engines emit U+202F before the day period and U+2009 in some
 *   range output where older engines (in-app webviews, Safari ≤18) emit a
 *   plain space. Normalising to a plain space on both sides makes every
 *   engine converge.
 *
 * Defaults to UTC to match the app-wide convention that naive event times
 * are venue wall-clock stored as UTC (see {@link parseTime}).
 */
export function formatTime(
  locale: string,
  date: Date,
  timeZone = 'UTC'
): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: hourCycleFor(locale),
    timeZone,
  })
    .format(date)
    .replace(/[\u202F\u2009\u00A0]/g, ' ');
}

/**
 * The canonical clock for each supported locale. Every locale this app ships
 * is 24-hour except US English — see `app/domain/i18n/strings/`. Bare `en`
 * resolves to h12 to keep `Intl`'s own CLDR default for it.
 */
function hourCycleFor(locale: string): 'h12' | 'h23' {
  const language = locale.toLowerCase();
  return language === 'en' || language.startsWith('en-us') ? 'h12' : 'h23';
}

export type FormattedPriceInput = {
  vatInclusivePriceCents: number;
  vatExclusivePriceCents: number;
  currency: string;
  exponent: number;
  /** When true, the price carries no VAT — the "excl. VAT" label is
   *  suppressed (a VAT-exempt price has no VAT to exclude). */
  vatExempt: boolean;
};

/**
 * The ONLY sanctioned currency formatting in the app: formats a signed amount
 * (in major units) as a localized currency string. Whole values drop their
 * ".00" — decimals only show when they deviate from .00 (US-10997), so
 * "DKK 149.00" renders as "DKK 149" while "DKK 149.50" keeps its decimals.
 *
 * Explicit digit counts rather than `trailingZeroDisplay: 'stripIfInteger'`:
 * engines that predate that option silently ignore it, and since prices render
 * during both SSR and hydration, a server/browser disagreement would be a
 * React #418 hydration mismatch (same class of bug as US-10915).
 */
export function formatCurrency(
  value: number,
  currency: string,
  locale: string
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...(Number.isInteger(value) && {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  }).format(value);
}

/**
 * Renders a signed amount (in major units) via {@link formatCurrency}. For
 * already-resolved amounts like price-breakdown lines; domain prices carrying
 * both VAT variants render with {@link FormattedPrice} instead.
 */
export function FormattedCurrency({
  value,
  currency,
}: {
  value: number;
  currency: string;
}) {
  const intl = useIntl();
  // Some browsers word prices differently from the server ("kr 6 490" for
  // "6 490 kr"): keep the server's wording rather than re-render the page.
  return (
    <span suppressHydrationWarning>
      {formatCurrency(value, currency, intl.locale)}
    </span>
  );
}

/**
 * Pure helper that formats a price as a localized currency string. Whole
 * values drop their ".00" (see {@link formatCurrency}).
 *
 * - `price = null` → renders "0" as a plain decimal (no currency)
 * - `includeVat = true` (default) → formats the VAT-inclusive (gross) value
 * - `includeVat = false` → formats the VAT-exclusive (net) value
 *
 * Uses native `Intl.NumberFormat` so it can be unit-tested without a React tree.
 */
export function formatPrice(
  price: FormattedPriceInput | null,
  locale: string,
  includeVat = true
): string {
  if (!price) {
    return new Intl.NumberFormat(locale, { style: 'decimal' }).format(0);
  }
  const cents = includeVat
    ? price.vatInclusivePriceCents
    : price.vatExclusivePriceCents;
  return formatCurrency(cents / 10 ** price.exponent, price.currency, locale);
}

/**
 * Renders a price as a localized currency string, respecting the storefront's
 * VAT-display preference (`includeTaxInPrices`).
 *
 * - In incl-VAT mode → shows the gross price as-is.
 * - In ex-VAT mode → shows the net price with a localized "excl. VAT" suffix
 *   (unless `vatLabel={false}` is passed, e.g. inside a breakdown where the VAT
 *   line already makes the exclusion explicit, or the price is VAT-exempt).
 *
 * Pass `null` to render "0" as a plain decimal — useful when a variant has no
 * price configured yet.
 *
 * @example
 * <FormattedPrice price={variant.price} />
 */
export function FormattedPrice({
  price,
  vatLabel = true,
}: {
  price: FormattedPriceInput | null;
  /** Append a localized "excl. VAT" suffix in ex-VAT mode. Pass false inside
   *  the price breakdown, where the VAT line already makes it explicit. */
  vatLabel?: boolean;
}) {
  const intl = useIntl();
  const includeVat = useIncludeTaxInPrices();
  const formatted = formatPrice(price, intl.locale, includeVat);
  // A VAT-exempt price has no VAT to exclude — suppress the label (net==gross
  // for exempt, so the formatted number is unaffected). The span keeps the
  // server's wording through hydration, as in FormattedCurrency.
  if (includeVat || !vatLabel || price === null || price.vatExempt)
    return <span suppressHydrationWarning>{formatted}</span>;
  return (
    <span suppressHydrationWarning>
      {formatted}{' '}
      <FormattedMessage
        defaultMessage="excl. VAT"
        description="Suffix after a price when the storefront displays prices excluding VAT"
      />
    </span>
  );
}

export function FormattedDate({ date }: { date: string }) {
  return (
    <span
      className={css({
        '&:first-letter': {
          textTransform: 'capitalize',
        },
      })}
      suppressHydrationWarning
    >
      <IntlFormattedDate
        value={date}
        weekday="long"
        month="long"
        day="numeric"
        timeZone="UTC"
      />
    </span>
  );
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * 24;
const MINUTES_PER_HALF_DAY = MINUTES_PER_DAY / 2;

/**
 * Pure helper that formats a whole-minute duration into a localized,
 * human-friendly label so a long experience doesn't read as a raw minute
 * count. Per product direction (US-10431), with the minutes tier shortened
 * per PR review feedback — only minutes abbreviate; hours and days stay
 * spelled out:
 *   - up to 1 hour  → shortened minutes   e.g. "45 min"
 *   - 1–24 hours    → clock-style hours    e.g. "1:30 hours", "2 hours"
 *   - 24 hours+     → days, half-day step  e.g. "1 day", "1.5 days", "2 days"
 *
 * The day tier rounds to the nearest half day (24–29h → 1, 30–41h → 1.5,
 * 42–48h → 2). Takes `intl` so it can be unit-tested without a React tree.
 */
export function formatDuration(intl: IntlShape, totalMinutes: number): string {
  if (totalMinutes >= MINUTES_PER_DAY) {
    const days = Math.round(totalMinutes / MINUTES_PER_HALF_DAY) / 2;
    return intl.formatMessage(
      {
        defaultMessage: '{days, plural, one {# day} other {# days}}',
        description:
          'Experience duration in days, e.g. "1.5 days" (title pill)',
      },
      { days }
    );
  }

  if (totalMinutes >= MINUTES_PER_HOUR) {
    const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = totalMinutes % MINUTES_PER_HOUR;
    // Clock-style "H:MM"; drop the ":00" for whole hours (e.g. "2 hours").
    const time =
      minutes === 0
        ? String(hours)
        : `${hours}:${String(minutes).padStart(2, '0')}`;
    return intl.formatMessage(
      {
        defaultMessage: `{time, plural, one {{time} hour} other {{time} hours}}`,
        description:
          'Experience duration in hours, clock-style, e.g. "1:30 hours" or "2 hours" (title pill)',
      },
      { time }
    );
  }

  return intl.formatMessage(
    {
      // Both plural arms render "min" in English — the abbreviated unit is
      // invariant here — but the plural form is kept anyway so translators
      // can supply a distinct form per locale where their plural rules
      // require it.
      defaultMessage: '{minutes, plural, one {# min} other {# min}}',
      description:
        'Experience duration in minutes, abbreviated unit; hours and days stay spelled out, e.g. "30 min" (duration chip/pill)',
    },
    { minutes: totalMinutes }
  );
}

/**
 * Renders an experience's duration (given in whole minutes) as a localized,
 * human-friendly label. See {@link formatDuration} for the unit rules.
 *
 * @example
 * <FormattedDuration minutes={90} /> // → "1:30 hours"
 */
export function FormattedDuration({ minutes }: { minutes: number }) {
  const intl = useIntl();
  return <>{formatDuration(intl, minutes)}</>;
}

/**
 * Joins alternatives with a localized "or": ["Samsø", "Aarhus", "Valby"] →
 * "Samsø, Aarhus or Valby" (US-10985). Hand-rolled rather than
 * `Intl.ListFormat`: ICU list data differs between engines (en-US Node adds
 * an Oxford comma where some browsers don't), and these labels render during
 * both SSR and hydration — a disagreement would be a React #418 hydration
 * mismatch (US-10915). A plain message also lets translators own the joiner.
 */
export function formatDisjunctionList(
  intl: IntlShape,
  items: readonly string[]
): string {
  const last = items.at(-1);
  if (last === undefined) return '';
  if (items.length === 1) return last;
  return intl.formatMessage(
    {
      defaultMessage: '{items} or {last}',
      description:
        'Joins alternatives, e.g. "Samsø, Aarhus or Valby" — {items} is the comma-joined head of the list, {last} its final entry',
    },
    { items: items.slice(0, -1).join(', '), last }
  );
}

/**
 * Upstream order is not one the page can rely on (storefronts-core and its
 * experiences-core fallback order these lists differently), so the app
 * imposes its own on the rendered label (US-14939). Collated rather than byte-sorted
 * so ø, å and ñ land where a reader looks for them.
 *
 * The options are pinned for the same reason {@link formatTime} pins
 * `hourCycle`: these render during both SSR and hydration, and left unpinned
 * a `-u-co-`/`-u-kn-`/`-u-kf-` extension on the locale tag re-tailors one side.
 */
export function sortLabels(
  locale: string,
  labels: readonly string[]
): string[] {
  const collator = new Intl.Collator(locale, {
    usage: 'sort',
    sensitivity: 'variant',
    numeric: false,
    caseFirst: 'false',
  });
  return [...labels].sort(collator.compare);
}

const MAX_LOCATION_LABELS = 3;

/**
 * One-line summary of an experience's distinct place labels (from
 * {@link formatLocationLabels}): up to three listed in full ("Samsø, Aarhus
 * or Valby"); past three, the first two plus a count of the rest ("Samsø,
 * Aarhus or 3 more") so hosts with many locations don't wrap the row
 * (US-10985).
 *
 * Sorted first, so which two a long list names is a property of the places
 * rather than of the order upstream supplied them in.
 */
export function formatLocationsSummary(
  intl: IntlShape,
  labels: readonly string[]
): string {
  const sorted = sortLabels(intl.locale, labels);
  const [first, second] = sorted;
  if (
    sorted.length <= MAX_LOCATION_LABELS ||
    first === undefined ||
    second === undefined
  ) {
    return formatDisjunctionList(intl, sorted);
  }
  const more = intl.formatMessage(
    {
      defaultMessage: '{count} more',
      description:
        'Count of additional experience locations hidden from the summary pill, e.g. "Samsø, Aarhus or 3 more"',
    },
    { count: sorted.length - 2 }
  );
  return formatDisjunctionList(intl, [first, second, more]);
}

type DurationTier = 'minutes' | 'hours' | 'days';

/**
 * A duration split into its display tier and rendered value, using the same
 * tier rules as {@link formatDuration}. `numeric` is the value in the tier's
 * own unit (e.g. 1.5 for "1:30 hours"), used to pluralize the shared unit
 * when several values collapse into one label.
 */
function durationParts(
  intl: IntlShape,
  totalMinutes: number
): { tier: DurationTier; value: string; numeric: number; minutes: number } {
  if (totalMinutes >= MINUTES_PER_DAY) {
    const days = Math.round(totalMinutes / MINUTES_PER_HALF_DAY) / 2;
    return {
      tier: 'days',
      value: intl.formatNumber(days),
      numeric: days,
      minutes: totalMinutes,
    };
  }
  if (totalMinutes >= MINUTES_PER_HOUR) {
    const wholeHours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = totalMinutes % MINUTES_PER_HOUR;
    const value =
      minutes === 0
        ? String(wholeHours)
        : `${wholeHours}:${String(minutes).padStart(2, '0')}`;
    return {
      tier: 'hours',
      value,
      numeric: totalMinutes / MINUTES_PER_HOUR,
      minutes: totalMinutes,
    };
  }
  return {
    tier: 'minutes',
    value: intl.formatNumber(totalMinutes),
    numeric: totalMinutes,
    minutes: totalMinutes,
  };
}

/**
 * Wraps an already-joined duration range ("1–2", "4–8") in its shared,
 * localized unit. `longest` (the longest duration, in the tier's own unit)
 * picks the plural form.
 */
function formatDurationValuesWithUnit(
  intl: IntlShape,
  tier: DurationTier,
  values: string,
  longest: number
): string {
  switch (tier) {
    case 'days':
      return intl.formatMessage(
        {
          defaultMessage:
            '{longest, plural, one {{values} day} other {{values} days}}',
          description:
            'A range of experience durations sharing the day unit, e.g. "1–2 days" — {values} is the joined numbers, {longest} the longest duration in days',
        },
        { values, longest }
      );
    case 'hours':
      return intl.formatMessage(
        {
          defaultMessage:
            '{longest, plural, one {{values} hour} other {{values} hours}}',
          description:
            'A range of experience durations sharing the hour unit, e.g. "4–8 hours" — {values} is the joined numbers, {longest} the longest duration in hours',
        },
        { values, longest }
      );
    case 'minutes':
      return intl.formatMessage(
        {
          defaultMessage:
            '{longest, plural, one {{values} min} other {{values} min}}',
          description:
            'A range of experience durations sharing the abbreviated minute unit, e.g. "15–45 min" — {values} is the joined numbers, {longest} the longest duration in minutes',
        },
        { values, longest }
      );
  }
}

function formatDurationRange(
  intl: IntlShape,
  from: string,
  to: string
): string {
  return intl.formatMessage(
    {
      defaultMessage: '{from}–{to}',
      description:
        'Range from the shortest to the longest experience duration, e.g. "4–8" (wrapped in a unit) or "45 minutes–2 days"',
    },
    { from, to }
  );
}

/**
 * Formats an experience's offered durations as one label (US-10985):
 *   - one value  → same as {@link formatDuration}, e.g. "1 hour"
 *   - several    → always a shortest–longest range, unit collapsed when both
 *                  ends share a tier: "1–2 hours", "4–8 hours"; full labels
 *                  across tiers: "30 minutes–2 hours"
 *
 * Values are deduped on their rendered form (half-day rounding can collapse
 * distinct minute counts). Returns `null` when nothing usable remains.
 */
export function formatDurationOptions(
  intl: IntlShape,
  minutesList: readonly number[]
): string | null {
  const distinct = [...new Set(minutesList)]
    .filter((m) => m > 0)
    .sort((a, b) => a - b);
  if (distinct.length === 0) return null;

  const seen = new Set<string>();
  const parts = distinct
    .map((m) => durationParts(intl, m))
    .filter((p) => {
      const key = `${p.tier}:${p.value}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  const shortest = parts[0];
  const longest = parts.at(-1);
  if (shortest === undefined || longest === undefined) return null;
  if (parts.length === 1) return formatDuration(intl, shortest.minutes);

  return shortest.tier === longest.tier
    ? formatDurationValuesWithUnit(
        intl,
        longest.tier,
        formatDurationRange(intl, shortest.value, longest.value),
        longest.numeric
      )
    : formatDurationRange(
        intl,
        formatDuration(intl, shortest.minutes),
        formatDuration(intl, longest.minutes)
      );
}
